import type { CarePlanCycleDetail } from "@/types/care-plan";

import type { CarePlanStep } from "./types";

interface CycleNesting {
  /** Existing cycle that should be the parent of the new cycle (new is nested inside). */
  parentCycleId: string | null;
  /** Existing cycles whose steps are strictly within the new cycle's range (should become children). */
  childCycleIds: string[];
}

/**
 * Detect parent/child cycle relationships for a new cycle being created.
 *
 * - **Parent**: an existing cycle whose steps fully contain the new range
 *   (smallest such cycle wins).
 * - **Children**: existing cycles whose steps are strictly inside the new
 *   range (only detected when no parent is found — a cycle can't wrap
 *   children if it's itself nested).
 */
export function detectCycleNesting({
  startIdx,
  endIdx,
  originalSteps,
  allSteps,
  cycles,
}: {
  startIdx: number;
  endIdx: number;
  originalSteps: CarePlanStep[];
  allSteps: CarePlanStep[];
  cycles?: CarePlanCycleDetail[];
}): CycleNesting {
  const selectedStepIds = new Set(
    originalSteps.slice(startIdx, endIdx + 1).map((s) => s.id),
  );

  type Candidate = { id: string; rangeSize: number };
  let bestParent: Candidate | null = null;
  const childIds: string[] = [];

  // Only consider original (non-materialized) steps for nesting detection.
  // Materialized pass-2+ copies share the same cycleId and would inflate
  // the cycle's apparent step count, breaking containment checks.
  const nonMaterializedSteps = allSteps.filter(
    (s) => !(s as CarePlanStep & { sourceStepId?: string | null }).sourceStepId,
  );

  // Check saved cycles via step cycleId assignments
  if (cycles && cycles.length > 0) {
    for (const cycle of cycles) {
      const cycleStepIds = nonMaterializedSteps
        .filter(
          (s) =>
            (s as CarePlanStep & { cycleId?: string | null }).cycleId ===
            cycle.id,
        )
        .map((s) => s.id);

      if (cycleStepIds.length === 0) continue;

      // Parent detection: all selected steps are within this cycle
      const allSelectedWithin = Array.from(selectedStepIds).every((id) =>
        new Set(cycleStepIds).has(id),
      );
      if (allSelectedWithin) {
        if (!bestParent || cycleStepIds.length < bestParent.rangeSize) {
          bestParent = { id: cycle.id, rangeSize: cycleStepIds.length };
        }
        continue;
      }

      // Child detection: all cycle steps are within the selected range
      const allCycleWithin = cycleStepIds.every((id) =>
        selectedStepIds.has(id),
      );
      if (allCycleWithin && cycleStepIds.length < selectedStepIds.size) {
        childIds.push(cycle.id);
      }
    }
  }

  // When adopting children, keep only those whose current parent isn't
  // also being adopted — descendants stay nested under their existing
  // parent instead of being flattened up to the new outer cycle.
  const childIdSet = new Set(childIds);
  const topLevelChildIds = childIds.filter((id) => {
    const current = cycles?.find((c) => c.id === id);
    const parentId = current?.parentCycleId;
    return !parentId || !childIdSet.has(parentId);
  });

  return {
    parentCycleId: bestParent?.id ?? null,
    // Only report children when no parent was found
    childCycleIds: bestParent ? [] : topLevelChildIds,
  };
}
